import { gql, useSuspenseQuery } from '@apollo/client';

// Returns a record of feature flag names to whether they are enabled. Feature flag values will be refetched when the auth state changes.
// If passed a key that is not actually a feature flag, it will return false.
export const useFeatureFlagsEnabled = (features: Record<string, string>) => {
    const { data } = useSuspenseQuery<{
        featureFlags: { id: keyof typeof features; enabled: boolean }[];
    }>(
        gql`
            query FeatureFlags($features: [String!]!) {
                featureFlags(keys: $features) {
                    id
                    enabled
                }
            }
        `,
        {
            variables: { features: Object.values(features) },
        },
    );

    // Transform the response into key-value pairs as follows:
    // {
    //     [feature1]: true,
    //     [feature2]: false,
    //     ...
    //     [featureN]: [featureN is enabled]
    // }
    //
    // If passed a key that is not actually a feature flag, it will return false.
    const featureFlagsEnabled = data.featureFlags.reduce<Record<string, boolean>>(
        (acc, feature) => {
            acc[feature.id] = feature.enabled;
            return acc;
        },
        {},
    );
    return Object.keys(features).reduce<Record<string, boolean>>((acc, key) => {
        acc[key] = featureFlagsEnabled[features[key]];
        return acc;
    }, {});
};

// Convenience function for when you only have one feature flag.
export const useFeatureFlagEnabled = (feature: string) => {
    return useFeatureFlagsEnabled({ [feature]: feature })[feature];
};
